import React from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Stack, 
  Grid, 
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EventIcon from '@mui/icons-material/Event';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HomeWorkIcon from '@mui/icons-material/HomeWork';

// Recharts Imports
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid 
} from 'recharts';

import { getRequest } from 'Includes/api/apicall';
import { GET_URL } from 'Includes/urls';
import { Link } from 'react-router-dom';

const userHasVisitListAccess = () => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    if (userInfo.is_superuser) return true;
    if (Array.isArray(userInfo.permission_codenames) && userInfo.permission_codenames.length) {
      return userInfo.permission_codenames.includes('visible_company_visit_view');
    }
    const ownCodes = (userInfo.user_permissions || userInfo.permissions || []).map((p) =>
      typeof p === 'string' ? p : p?.codename
    );
    const groupCodes = (userInfo.groups || [])
      .flatMap((g) => g?.permissions || [])
      .map((p) => (typeof p === 'string' ? p : p?.codename));
    const set = new Set([...ownCodes, ...groupCodes].filter(Boolean));
    return set.has('visible_company_visit_view');
  } catch {
    return false;
  }
};

const userHasCompanyListMenuAccess = () => {
  try {
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    if (userInfo.is_superuser) return true;
    if (Array.isArray(userInfo.permission_codenames) && userInfo.permission_codenames.length) {
      return userInfo.permission_codenames.includes('visible_company_list_view');
    }
    const ownCodes = (userInfo.user_permissions || userInfo.permissions || []).map((p) =>
      typeof p === 'string' ? p : p?.codename
    );
    const groupCodes = (userInfo.groups || [])
      .flatMap((g) => g?.permissions || [])
      .map((p) => (typeof p === 'string' ? p : p?.codename));
    const set = new Set([...ownCodes, ...groupCodes].filter(Boolean));
    return set.has('visible_company_list_view');
  } catch {
    return false;
  }
};

const CLIENT_STATUS_COLORS = {
  Active: '#16a34a',
  'Low Engagement': '#ca8a04',
  'At Risk': '#ea580c',
  Dead: '#64748b',
  Unknown: '#94a3b8',
};

const enrichClientStatusChartData = (rows) =>
  (Array.isArray(rows) ? rows : []).map((d) => ({
    ...d,
    color: CLIENT_STATUS_COLORS[d.name] || CLIENT_STATUS_COLORS.Unknown,
  }));

const BRAND = '#4680FF';
const BRAND_DARK = '#1665D8';
const CHART_COLORS = ['#7c3aed', '#2563eb', '#0d9488', '#16a34a', '#ca8a04', '#dc2626', '#db2777', '#334155'];

class DashboardView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      clientDashboard: {
        activeClients: [],
        inactiveClients: [],
        deadClients: [],
        teamActiveClients: [],
        teamAtRiskClients: [],
        teamDeadClients: [],
        highPayingCompanies: [],
        userGroupCounts: [],
        ticketStatusCounts: [],
        coordinatorCounts: [],
        ticketsByCompanyCounts: [],
        taskStatusCounts: [],
        clientStatusByTeam: [],
        clientStatusBySystem: [],
        onLeaveToday: [],
      },
      clientLoading: false,
      nearestVisits: [],
      nearestVisitsLoading: false,
    };
  }

  componentDidMount() {
    this.loadClientDashboard();
    this.loadNearestVisits();
  }

  loadNearestVisits = async () => {
    if (!userHasVisitListAccess()) return;
    this.setState({ nearestVisitsLoading: true });
    try {
      const res = await getRequest(
        GET_URL.company_visits.api,
        {
          limit: 10,
          pageno: 1,
          ordering: 'visit_date',
          nearest: '1',
        },
        this.props
      );
      const list = res?.data?.data;
      this.setState({
        nearestVisits: Array.isArray(list) ? list : [],
        nearestVisitsLoading: false,
      });
    } catch (e) {
      console.error('Nearest visits load error:', e);
      this.setState({ nearestVisitsLoading: false });
    }
  };

  loadClientDashboard = async () => {
    this.setState({ clientLoading: true });
    try {
      const dashboardRes = await getRequest(GET_URL.dashboard.api, {}, this.props);
      const dashboardData = dashboardRes?.data?.data || {};

      this.setState({
        clientDashboard: {
          activeClients: dashboardData.activeClients || [],
          inactiveClients: dashboardData.inactiveClients || [],
          deadClients: dashboardData.deadClients || [],
          teamActiveClients: dashboardData.teamActiveClients || [],
          teamAtRiskClients: dashboardData.teamAtRiskClients || [],
          teamDeadClients: dashboardData.teamDeadClients || [],
          userGroupCounts: dashboardData.userGroupCounts || [],
          ticketStatusCounts: dashboardData.ticketStatusCounts || [],
          coordinatorCounts: dashboardData.coordinatorCounts || [],
          ticketsByCompanyCounts: dashboardData.ticketsByCompanyCounts || [],
          taskStatusCounts: dashboardData.taskStatusCounts || [],
          clientStatusByTeam: dashboardData.clientStatusByTeam || [],
          clientStatusBySystem: dashboardData.clientStatusBySystem || [],
          onLeaveToday: dashboardData.onLeaveToday || [],
        },
        clientLoading: false,
      });
    } catch (error) {
      console.error("Dashboard Load Error:", error);
      this.setState({ clientLoading: false });
    }
  };

  renderDonutChart = (title, data) => {
    const validData = data.filter(d => d.value > 0);
    return (
      <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 3, height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
          <Box sx={{ height: 250, width: '100%' }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={validData}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {validData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Box>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {validData.map((item, i) => (
              <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color || CHART_COLORS[i % CHART_COLORS.length] }} />
                  <Typography variant="caption">{item.name}</Typography>
                </Stack>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>{item.value}</Typography>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>
    );
  };

  renderBarChart = (title, data, color) => (
    <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>{title}</Typography>
        <Box sx={{ height: 250, width: '100%' }}>
          <ResponsiveContainer>
            <BarChart data={data} layout="vertical" margin={{ left: 30, right: 30 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" hide />
              <YAxis dataKey="name" type="category" width={80} style={{ fontSize: '12px' }} />
              <Tooltip cursor={{ fill: 'transparent' }} />
              <Bar dataKey="value" name={title == 'Tasks by Stage' ? 'tasks' : 'tickets'} fill={color} radius={[0, 4, 4, 0]} barSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );

  renderOnLeaveToday = () => {
    const people = this.state.clientDashboard.onLeaveToday || [];
    const onLeave = people.filter((row) => row.leave_type !== 'work_from_home');
    const workingFromHome = people.filter((row) => row.leave_type === 'work_from_home');
    return (
      <Stack spacing={2}>
        <Card elevation={0} sx={{ border: '1px solid #f5d0a8', borderRadius: 3, bgcolor: '#fffaf3' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: onLeave.length ? 1.5 : 0 }}>
              <EventBusyIcon sx={{ color: '#c2410c' }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  On leave today
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {onLeave.length
                    ? `${onLeave.length} ${onLeave.length === 1 ? 'person is' : 'people are'} on leave today`
                    : 'No one is on leave today'}
                </Typography>
              </Box>
            </Stack>
            {onLeave.length ? (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {onLeave.map((row) => (
                  <Chip
                    key={row.id}
                    label={row.user_name}
                    sx={{ bgcolor: '#ffedd5', fontWeight: 600 }}
                  />
                ))}
              </Stack>
            ) : null}
          </CardContent>
        </Card>
        <Card elevation={0} sx={{ border: '1px solid #bfdbfe', borderRadius: 3, bgcolor: '#f8fbff' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: workingFromHome.length ? 1.5 : 0 }}>
              <HomeWorkIcon sx={{ color: '#1d4ed8' }} />
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Working from home today
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {workingFromHome.length
                    ? `${workingFromHome.length} ${workingFromHome.length === 1 ? 'person is' : 'people are'} working from home today`
                    : 'No one is working from home today'}
                </Typography>
              </Box>
            </Stack>
            {workingFromHome.length ? (
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {workingFromHome.map((row) => (
                  <Chip
                    key={row.id}
                    label={row.user_name}
                    sx={{ bgcolor: '#dbeafe', fontWeight: 600 }}
                  />
                ))}
              </Stack>
            ) : null}
          </CardContent>
        </Card>
      </Stack>
    );
  };

  formatVisitTime = (t) => {
    if (!t) return '—';
    if (typeof t === 'string' && t.length >= 5) return t.slice(0, 5);
    return String(t);
  };

  renderNearestVisits = () => {
    if (!userHasVisitListAccess()) return null;
    const { nearestVisits, nearestVisitsLoading } = this.state;
    const statusLabel = {
      pending: 'Pending',
      visited: 'Visited',
      postponed: 'Postponed',
      cancel: 'Canceled',
    };

    return (
      <Grid item xs={12}>
        <Card elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 3 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <EventIcon sx={{ color: BRAND }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Upcoming visits
                  </Typography>
                </Box>
              </Stack>
              <Box
                component={Link}
                to="/visits/list"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontWeight: 700,
                  fontSize: '0.875rem',
                  color: BRAND,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                View all
                <ArrowForwardIcon sx={{ fontSize: 18 }} />
              </Box>
            </Stack>
            {nearestVisitsLoading ? <LinearProgress /> : null}
            <TableContainer component={Paper} variant="outlined" sx={{ mt: nearestVisitsLoading ? 2 : 0, borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fb' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Visit date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Assigned to</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {nearestVisits.length === 0 && !nearestVisitsLoading ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">
                          No upcoming visits scheduled.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    nearestVisits.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.visit_date || '—'}</TableCell>
                        <TableCell>{this.formatVisitTime(row.visit_time)}</TableCell>
                        <TableCell>{row.company_name || '—'}</TableCell>
                        <TableCell>{statusLabel[row.status] || row.status || '—'}</TableCell>
                        <TableCell>{row.assigned_to_name || '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  render() {
    const { clientDashboard, clientLoading } = this.state;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const companyListAllowed = userHasCompanyListMenuAccess();

    return (
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
          <DashboardIcon sx={{ fontSize: 40, color: BRAND }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Welcome, {user.username || ''}</Typography>
        </Stack>

        {clientLoading && <LinearProgress sx={{ mb: 3 }} />}

        <Grid container spacing={3}>
          <Grid item xs={12}>
            {this.renderOnLeaveToday()}
          </Grid>
          {/* Summary Cards */}
          <Grid item xs={12} md={4}>
            <MetricCard
              label="Active Client By Team"
              count={clientDashboard.teamActiveClients.length}
              color="#16a34a"
              linkTo={companyListAllowed ? '/company/list?status=active&status_by_team=active' : null}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard
              label="At Risk Client By Team"
              count={clientDashboard.teamAtRiskClients.length}
              color="#ea580c"
              linkTo={companyListAllowed ? '/company/list?status=active&status_by_team=at_risk' : null}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard
              label="Dead Client By Team"
              count={clientDashboard.teamDeadClients.length}
              color="#dc2626"
              linkTo={companyListAllowed ? '/company/list?status=active&status_by_team=dead' : null}
            />
          </Grid>

          {this.renderNearestVisits()}

          {/* Client status (team vs system) — same categories as company profile */}
          <Grid item xs={12} md={6}>
            {this.renderDonutChart(
              'Client status (by team)',
              enrichClientStatusChartData(clientDashboard.clientStatusByTeam)
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            {this.renderDonutChart(
              'Client status (by system)',
              enrichClientStatusChartData(clientDashboard.clientStatusBySystem)
            )}
          </Grid>

          {/* Charts Row 1 */}
          <Grid item xs={12} md={6}>
            {this.renderDonutChart('Client status by team (Active / At Risk / Dead)', [
              { name: 'Active', value: clientDashboard.teamActiveClients.length, color: '#16a34a' },
              { name: 'At Risk', value: clientDashboard.teamAtRiskClients.length, color: '#ea580c' },
              { name: 'Dead', value: clientDashboard.teamDeadClients.length, color: '#dc2626' },
            ])}
          </Grid>
          <Grid item xs={12} md={6}>
            {this.renderDonutChart('Users by Role', clientDashboard.userGroupCounts)}
          </Grid>
          <Grid item xs={12} md={6}>
            {this.renderDonutChart('Companies by Coordinator', clientDashboard.coordinatorCounts)}
          </Grid>

          {/* Charts Row 2 */}
          <Grid item xs={12} md={6}>
            {this.renderBarChart('Ticket Status', clientDashboard.ticketStatusCounts, '#2563eb')}
          </Grid>
          <Grid item xs={12} md={6}>
            {this.renderBarChart('Tasks by Stage', clientDashboard.taskStatusCounts, '#7c3aed')}
          </Grid>

          {/* Charts Row 3 */}
          <Grid item xs={12}>
            {this.renderBarChart('Tickets by Company (Top 10)', clientDashboard.ticketsByCompanyCounts, '#0d9488')}
          </Grid>
        </Grid>
      </Box>
    );
  }
}

const MetricCard = ({ label, count, color, linkTo }) => {
  const inner = (
    <CardContent sx={{ py: 2.5, px: 2.5, '&:last-child': { pb: 2.5 } ,borderRadius: 3,border: '1px solid skyblue'}}>
      <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em', fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h3" sx={{ fontWeight: 800, color, mt: 0.5, lineHeight: 1.15 }}>
        {count}
      </Typography>
      {linkTo ? (
        <Box
          sx={{
            mt: 1.75,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.25,
            py: 0.65,
            borderRadius: 999,
            bgcolor: 'rgba(70, 128, 255, 0.08)',
            color: BRAND,
            fontSize: '0.8125rem',
            fontWeight: 700,
          }}
        >
          Open company list
          <ArrowForwardIcon sx={{ fontSize: 18, opacity: 0.9 }} />
        </Box>
      ) : null}
    </CardContent>
  );
  const cardSx = {
    borderRadius: 3,
    textDecoration: 'none',
    color: 'inherit',
    border: '1px solid',
    borderColor: 'rgba(15, 23, 42, 0.08)',
    boxShadow: '0 2px 12px rgba(15, 23, 42, 0.06)',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
    ...(linkTo
      ? {
          '&:hover': {
            borderColor: BRAND,
            boxShadow: '0 8px 28px rgba(70, 128, 255, 0.22)',
            transform: 'translateY(-2px)',
          },
        }
      : {}),
  };
  if (linkTo) {
    return (
      <Card component={Link} to={linkTo} elevation={0} sx={cardSx}>
        {inner}
      </Card>
    );
  }
  return (
    <Card elevation={0} sx={cardSx}>
      {inner}
    </Card>
  );
};

export default DashboardView;
